export default function TitusvilleDestinationsSection() {
  const stats = [
    {
      value: "70%",
      label: "of Titusville requests are aerospace-related travel or cruise departures with under 3 weeks notice",
    },
    {
      value: "1–3 weeks",
      label: "average notice for launch campaign travel, payload integration reviews, and mission support trips",
    },
    {
      value: "Year-round",
      label: "SpaceX high-cadence launches create continuous demand — passport urgency no longer seasonal",
    },
    {
      value: "5 days",
      label: "shortest launch photography credential window we've accommodated for a Titusville photographer",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "French Guiana",
      context: "Top international destination for Titusville aerospace professionals and launch photographers. The Guiana Space Centre in Kourou is the primary launch site for Ariane rockets and a frequent destination for NASA and contractor personnel supporting international payload integration. French Guiana is an overseas department of France — valid passport required with six-month validity rule.",
      urgency: "Launch campaigns shift constantly — payload reviews and launch support trips confirmed with 1–3 weeks of notice",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas",
      context: "Most common cruise destination from Port Canaveral for Titusville families. Also popular for Titusville anglers heading to bonefishing flats in Andros and the Abacos. Recreational boaters from Titusville make regular runs to Grand Bahama and Bimini. All travelers need valid passports for entry and re-entry.",
      urgency: "Last-minute cruise bookings from Port Canaveral often come together with 3–7 days of notice",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Germany & France",
      context: "Frequent destinations for Titusville aerospace engineers attending Airbus and ESA partner meetings in Toulouse, Bremen, and Paris. Also popular with space industry professionals presenting at IAC (International Astronautical Congress) and attending satellite integration reviews with European partners.",
      urgency: "Engineering reviews and conference invitations arrive 3–6 weeks before departure — tight for standard processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Japan",
      context: "JAXA (Japan Aerospace Exploration Agency) coordination meetings and ISS program reviews in Tokyo and Tsukuba are regular travel for Titusville NASA and contractor personnel. Japan requires passports valid for the duration of stay with strict entry requirements.",
      urgency: "Mission planning meetings and partner reviews are confirmed with 2–4 weeks of notice",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Caribbean Cruises",
      context: "Titusville families regularly cruise from Port Canaveral to the Eastern and Western Caribbean — Jamaica, Cayman Islands, Cozumel, St. Thomas, St. Maarten. Disney Cruise Line from Port Canaveral is especially popular. All passengers, including children, need valid passports for most itineraries.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing need emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "New Zealand",
      context: "Growing destination for Titusville launch photographers and space industry professionals covering Rocket Lab Electron launches from the Mahia Peninsula. New Zealand's remote location and strict entry requirements make passport validity critical — no exceptions for expired or near-expired documents.",
      urgency: "Launch manifest changes and credential approvals confirmed with 1–2 weeks of notice",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Titusville Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Titusville
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Titusville residents head to French Guiana and Europe for aerospace launch campaigns, the Bahamas and Caribbean for cruises and fishing, Japan for space agency coordination, and New Zealand for launch photography. These destinations create the most passport urgency for our Space Coast clients.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout the Space Coast</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Cocoa Beach", href: "/cocoa-beach" },
              { name: "Melbourne", href: "/melbourne" },
              { name: "Orlando", href: "/orlando" },
              { name: "Daytona Beach", href: "/daytona-beach" },
              { name: "Stuart", href: "/stuart" },
              { name: "Port St. Lucie", href: "/port-st-lucie" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}